import React, { useState, useEffect, useCallback } from 'react';
import { Button, notification, DatePicker } from 'antd';
import { Modal } from '_src/components';
import { Select, Input } from '_src/shared/form-helpers';
import { useFetch, useSave } from 'store';
import services from '_src/services';
import moment from 'moment';

import { ModalContent, FormGrid, FormRow, Label } from '../styles';

const laptopConfig = services['laptopLending'];

export default function AddEditLaptopModal({
  visible,
  onClose,
  laptop = null,
  branchId,
  onSaved
}) {
  const isEdit = !!laptop;

  const [form, setForm] = useState({
    brand: '',
    model: '',
    serial_number: '',
    asset_tag: '',
    purchase_date: null,
    purchase_price: '',
    vendor: '',
    warranty_expiration: null,
    specifications: '',
    notes: ''
  });

  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    trackPurchaseDate: false,
    trackPurchasePrice: false,
    trackVendor: false,
    trackWarranty: false,
    trackSpecifications: false,
    useAssetTags: false
  });

  const { save: createLaptop } = useSave(laptopConfig.createLaptop);
  const { save: updateLaptop } = useSave(laptopConfig.updateLaptop);
  const { get: fetchSettings } = useFetch(laptopConfig.getSettings);

  const loadSettings = useCallback(async () => {
    if (!branchId) return;
    try {
      const response = await fetchSettings({ params: { branch_id: branchId } });
      const settingsList = response?.settings || response?.[0]?.data?.settings || [];
      const findSetting = (key) => settingsList.find(s => s.setting_key === key);

      setSettings({
        trackPurchaseDate: findSetting('track_purchase_date')?.enabled || false,
        trackPurchasePrice: findSetting('track_purchase_price')?.enabled || false,
        trackVendor: findSetting('track_vendor')?.enabled || false,
        trackWarranty: findSetting('track_warranty')?.enabled || false,
        trackSpecifications: findSetting('track_specifications')?.enabled || false,
        useAssetTags: findSetting('use_asset_tags')?.enabled || false
      });
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  }, [branchId]);

  useEffect(() => {
    if (visible) {
      loadSettings();
    }
  }, [visible, loadSettings]);

  useEffect(() => {
    if (laptop) {
      setForm({
        brand: laptop.brand || '',
        model: laptop.model || '',
        serial_number: laptop.serialNumber || laptop.serial_number || '',
        asset_tag: laptop.assetTag || laptop.asset_tag || '',
        purchase_date: (laptop.purchaseDate || laptop.purchase_date) ? moment(laptop.purchaseDate || laptop.purchase_date) : null,
        purchase_price: laptop.purchasePrice || laptop.purchase_price || '',
        vendor: laptop.vendor || '',
        warranty_expiration: (laptop.warrantyExpiration || laptop.warranty_expiration) ? moment(laptop.warrantyExpiration || laptop.warranty_expiration) : null,
        specifications: laptop.specifications || '',
        notes: laptop.notes || laptop.conditionNotes || ''
      });
    } else {
      setForm({
        brand: '',
        model: '',
        serial_number: '',
        asset_tag: '',
        purchase_date: null,
        purchase_price: '',
        vendor: '',
        warranty_expiration: null,
        specifications: '',
        notes: ''
      });
    }
  }, [laptop, visible]);

  const handleSave = async () => {
    if (!form.brand || !form.model || !form.serial_number) {
      notification.warning({ message: 'Please fill in required fields', duration: 3 });
      return;
    }

    setSaving(true);

    const payload = {
      brand: form.brand,
      model: form.model,
      serial_number: form.serial_number,
      notes: form.notes,
      branch_id: branchId
    };

    if (settings.useAssetTags) {
      payload.asset_tag = form.asset_tag;
    }
    if (settings.trackPurchaseDate) {
      payload.purchase_date = form.purchase_date ? form.purchase_date.format('YYYY-MM-DD') : null;
    }
    if (settings.trackPurchasePrice) {
      payload.purchase_price = form.purchase_price ? parseFloat(form.purchase_price) : null;
    }
    if (settings.trackVendor) {
      payload.vendor = form.vendor;
    }
    if (settings.trackWarranty) {
      payload.warranty_expiration = form.warranty_expiration ? form.warranty_expiration.format('YYYY-MM-DD') : null;
    }
    if (settings.trackSpecifications) {
      payload.specifications = form.specifications;
    }

    let response;
    if (isEdit) {
      response = await updateLaptop(payload, { replace: { laptopId: laptop.id } });
    } else {
      response = await createLaptop(payload);
    }

    setSaving(false);

    if (response?.success === 1) {
      notification.success({
        message: isEdit ? 'Laptop updated successfully' : 'Laptop added successfully',
        duration: 5
      });
      onSaved();
      onClose();
    } else {
      notification.error({
        message: response?.data?.message || 'Failed to save laptop',
        duration: 5
      });
    }
  };

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Modal
      title={isEdit ? 'Edit Laptop' : 'Add New Laptop'}
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSave} loading={saving}>
          {isEdit ? 'Update' : 'Add Laptop'}
        </Button>
      ]}
      width={700}
    >
      <ModalContent>
        <FormGrid>
          <FormRow>
            <Label>Brand *</Label>
            <Input
              name="brand"
              placeholder="e.g. Dell, HP, Lenovo"
              value={form.brand}
              onChange={value => updateField('brand', value)}
            />
          </FormRow>

          <FormRow>
            <Label>Model *</Label>
            <Input
              name="model"
              placeholder="e.g. Latitude 5520"
              value={form.model}
              onChange={value => updateField('model', value)}
            />
          </FormRow>

          <FormRow>
            <Label>Serial Number *</Label>
            <Input
              name="serial_number"
              placeholder="Serial number"
              value={form.serial_number}
              onChange={value => updateField('serial_number', value)}
              disabled={isEdit}
            />
          </FormRow>

          {settings.useAssetTags && (
            <FormRow>
              <Label>Asset Tag</Label>
              <Input
                name="asset_tag"
                placeholder="Internal asset tag"
                value={form.asset_tag}
                onChange={value => updateField('asset_tag', value)}
              />
            </FormRow>
          )}

          {settings.trackPurchaseDate && (
            <FormRow>
              <Label>Purchase Date</Label>
              <DatePicker
                style={{ width: '100%' }}
                placeholder="Select date"
                value={form.purchase_date}
                onChange={date => updateField('purchase_date', date)}
                format="YYYY-MM-DD"
              />
            </FormRow>
          )}

          {settings.trackPurchasePrice && (
            <FormRow>
              <Label>Purchase Price ($)</Label>
              <Input
                name="purchase_price"
                placeholder="0.00"
                value={form.purchase_price}
                onChange={value => updateField('purchase_price', value)}
                type="number"
              />
            </FormRow>
          )}

          {settings.trackVendor && (
            <FormRow>
              <Label>Vendor</Label>
              <Input
                name="vendor"
                placeholder="Vendor or supplier name"
                value={form.vendor}
                onChange={value => updateField('vendor', value)}
              />
            </FormRow>
          )}

          {settings.trackWarranty && (
            <FormRow>
              <Label>Warranty Expiration</Label>
              <DatePicker
                style={{ width: '100%' }}
                placeholder="Warranty end date"
                value={form.warranty_expiration}
                onChange={date => updateField('warranty_expiration', date)}
                format="YYYY-MM-DD"
              />
            </FormRow>
          )}

          {settings.trackSpecifications && (
            <FormRow>
              <Label>Specifications</Label>
              <Input
                name="specifications"
                placeholder="CPU, RAM, storage, etc."
                value={form.specifications}
                onChange={value => updateField('specifications', value)}
              />
            </FormRow>
          )}

          <FormRow>
            <Label>Notes</Label>
            <Input
              name="notes"
              placeholder="Optional notes"
              value={form.notes}
              onChange={value => updateField('notes', value)}
            />
          </FormRow>
        </FormGrid>
      </ModalContent>
    </Modal>
  );
}
